import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Badge
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Assignment as CertificateIcon,
  Science as ScienceIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  CheckCircle as PassIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const TestCertificateReport = () => {
  const [filters, setFilters] = useState({
    from_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0],
    heat_no: '',
    mill_code: '',
    steel_code: '',
    certificate_type: 'all'
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: reportData, isLoading, error, refetch } = useQuery(
    ['testCertificateReport', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/reports/test-certificates?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching test certificate report: ${error.message}`, { variant: 'error' });
      },
      enabled: false
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleGenerateReport = () => {
    refetch();
  };

  const getComplianceStatus = (actual, min, max) => {
    if (min !== null && actual < min) return 'below';
    if (max !== null && actual > max) return 'above';
    return 'pass';
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case 'pass': return 'success';
      case 'below': return 'error';
      case 'above': return 'warning';
      default: return 'default';
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading test certificate report: {error.message}
        </Alert>
      </Box>
    );
  }

  const mockData = [
    {
      heat_no: 'H240915001',
      mill_code: 'SAIL',
      mill_name: 'Steel Authority of India Ltd',
      tc_no: 'TC/SAIL/24/0915',
      steel_code: 'EN24',
      certificate_date: '2024-09-15',
      carbon: 0.35,
      carbon_spec: { min: 0.30, max: 0.38 },
      manganese: 0.62,
      manganese_spec: { min: 0.45, max: 0.70 },
      silicon: 0.28,
      silicon_spec: { min: 0.10, max: 0.35 },
      chromium: 1.25,
      chromium_spec: { min: 1.00, max: 1.40 },
      nickel: 1.55,
      nickel_spec: { min: 1.30, max: 1.70 },
      molybdenum: 0.28,
      molybdenum_spec: { min: 0.20, max: 0.35 },
      tensile_strength: 985,
      tensile_spec: { min: 850, max: 1200 },
      yield_strength: 785,
      yield_spec: { min: 650, max: null },
      elongation: 18.5,
      elongation_spec: { min: 12.0, max: null },
      compliance_status: 'pass'
    },
    {
      heat_no: 'H240920002',
      mill_code: 'TATA',
      mill_name: 'Tata Steel Limited',
      tc_no: 'TC/TATA/24/0920',
      steel_code: 'EN8',
      certificate_date: '2024-09-20',
      carbon: 0.41,
      carbon_spec: { min: 0.36, max: 0.44 },
      manganese: 0.75,
      manganese_spec: { min: 0.60, max: 0.90 },
      silicon: 0.32,
      silicon_spec: { min: 0.10, max: 0.40 },
      chromium: null,
      chromium_spec: { min: null, max: null },
      nickel: null,
      nickel_spec: { min: null, max: null },
      molybdenum: null,
      molybdenum_spec: { min: null, max: null },
      tensile_strength: 680,
      tensile_spec: { min: 550, max: 750 },
      yield_strength: 380,
      yield_spec: { min: 300, max: null },
      elongation: 22.0,
      elongation_spec: { min: 16.0, max: null },
      compliance_status: 'pass'
    },
    {
      heat_no: 'H240925003',
      mill_code: 'JSW',
      mill_name: 'JSW Steel Limited',
      tc_no: 'TC/JSW/24/0925',
      steel_code: '20MnCr5',
      certificate_date: '2024-09-25',
      carbon: 0.25,
      carbon_spec: { min: 0.17, max: 0.23 },
      manganese: 1.28,
      manganese_spec: { min: 1.10, max: 1.40 },
      silicon: 0.35,
      silicon_spec: { min: 0.15, max: 0.40 },
      chromium: 1.18,
      chromium_spec: { min: 1.00, max: 1.30 },
      nickel: null,
      nickel_spec: { min: null, max: null },
      molybdenum: null,
      molybdenum_spec: { min: null, max: null },
      tensile_strength: 1050,
      tensile_spec: { min: 900, max: 1300 },
      yield_strength: 650,
      yield_spec: { min: 500, max: null },
      elongation: 15.2,
      elongation_spec: { min: 10.0, max: null },
      compliance_status: 'above'
    }
  ];

  const totalCertificates = mockData.length;
  const passedCertificates = mockData.filter(item => item.compliance_status === 'pass').length;
  const failedCertificates = mockData.filter(item => item.compliance_status !== 'pass').length;

  const formatValue = (value, spec) => {
    if (value === null) return '-';
    const status = getComplianceStatus(value, spec?.min, spec?.max);
    return {
      value: value.toFixed(spec?.min !== null && spec?.min < 1 ? 3 : 0),
      status
    };
  };

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CertificateIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" gutterBottom color="primary">
          Test Certificate Report
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Report Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Heat Number"
              value={filters.heat_no}
              onChange={(e) => setFilters({ ...filters, heat_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Mill Code"
              value={filters.mill_code}
              onChange={(e) => setFilters({ ...filters, mill_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Steel Code"
              value={filters.steel_code}
              onChange={(e) => setFilters({ ...filters, steel_code: e.target.value })}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              startIcon={<SearchIcon />}
            >
              Generate
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to Excel
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {totalCertificates}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Certificates
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.main">
                    {passedCertificates}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Compliant
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="error.main">
                    {failedCertificates}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Non-Compliant
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="info.main">
                    {((passedCertificates / totalCertificates) * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Compliance Rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Test Certificate Table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Mill Test Certificate Analysis
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Period: {filters.from_date} to {filters.to_date}
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Certificate Details</strong></TableCell>
                    <TableCell><strong>Steel Grade</strong></TableCell>
                    <TableCell align="center"><strong>Carbon %</strong></TableCell>
                    <TableCell align="center"><strong>Manganese %</strong></TableCell>
                    <TableCell align="center"><strong>Silicon %</strong></TableCell>
                    <TableCell align="center"><strong>Chromium %</strong></TableCell>
                    <TableCell align="center"><strong>Tensile (MPa)</strong></TableCell>
                    <TableCell align="center"><strong>Elongation %</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No test certificate data found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    mockData.map((record, index) => {
                      const carbonResult = formatValue(record.carbon, record.carbon_spec);
                      const manganeseResult = formatValue(record.manganese, record.manganese_spec);
                      const siliconResult = formatValue(record.silicon, record.silicon_spec);
                      const chromiumResult = formatValue(record.chromium, record.chromium_spec);
                      const tensileResult = formatValue(record.tensile_strength, record.tensile_spec);
                      const elongationResult = formatValue(record.elongation, record.elongation_spec);

                      return (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                {record.tc_no}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Heat: {record.heat_no}
                              </Typography>
                              <Typography variant="caption" display="block" color="textSecondary">
                                {record.mill_code} - {new Date(record.certificate_date).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={record.steel_code} 
                              color="primary" 
                              variant="outlined" 
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={carbonResult.value}
                              color={getComplianceColor(carbonResult.status)}
                              size="small"
                              variant={carbonResult.status === 'pass' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={manganeseResult.value}
                              color={getComplianceColor(manganeseResult.status)}
                              size="small"
                              variant={manganeseResult.status === 'pass' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={siliconResult.value}
                              color={getComplianceColor(siliconResult.status)}
                              size="small"
                              variant={siliconResult.status === 'pass' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={chromiumResult.value}
                              color={getComplianceColor(chromiumResult.status)}
                              size="small"
                              variant={chromiumResult.status === 'pass' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={tensileResult.value}
                              color={getComplianceColor(tensileResult.status)}
                              size="small"
                              variant={tensileResult.status === 'pass' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={elongationResult.value}
                              color={getComplianceColor(elongationResult.status)}
                              size="small"
                              variant={elongationResult.status === 'pass' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                              badgeContent={
                                record.compliance_status === 'pass' ? 
                                <PassIcon sx={{ fontSize: 16, color: 'success.main' }} /> :
                                <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                              }
                            >
                              <Chip 
                                label={record.compliance_status === 'pass' ? 'COMPLIANT' : 'NON-COMPLIANT'}
                                color={record.compliance_status === 'pass' ? 'success' : 'error'}
                                size="small"
                                icon={record.compliance_status === 'pass' ? <VerifiedIcon /> : <WarningIcon />}
                              />
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Compliance Legend */}
          <Paper sx={{ mt: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Test Certificate Compliance Guide
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip label="Within Spec" color="success" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">Values within specification limits</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip label="Above Spec" color="warning" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">Values above maximum limit</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip label="Below Spec" color="error" size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">Values below minimum limit</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default TestCertificateReport;